/**
 * Pure mappers from engine state to ui component props.
 * Kept out of the components so they are unit-testable without rendering.
 */

import {
  BUILDINGS,
  RESEARCH,
  UNITS,
  buildTimeHours,
  buildingLevel,
  canAfford,
  computeModifiers,
  fleetHangarCapacity,
  levelCost,
  productionReport,
  researchLevelCost,
  researchTimeHours,
  storageReport,
  tradeRelayCost,
  unitTimeHours,
  xpForNextLevel,
  type BuildingKey,
  type DefenseKey,
  type GameState,
  type QueueJob,
  type ResearchKey,
  type ShipKey,
  type UnitKey,
} from '@spacegame/engine';

export type CardState = 'locked' | 'available' | 'insufficient' | 'active' | 'damaged' | 'maxed';

function requirementsMet(
  game: GameState,
  requires?: (typeof BUILDINGS)[BuildingKey]['requires'],
  planetIndex = 0,
): boolean {
  if (!requires) return true;
  for (const [key, level] of Object.entries(requires.buildings ?? {})) {
    if (buildingLevel(game, key as BuildingKey, planetIndex) < (level as number)) return false;
  }
  for (const [key, level] of Object.entries(requires.research ?? {})) {
    if (game.research[key as ResearchKey] < (level as number)) return false;
  }
  return true;
}

/** Human-readable requirement line for locked cards. */
export function requirementText(
  requires?: (typeof BUILDINGS)[BuildingKey]['requires'],
): string | undefined {
  if (!requires) return undefined;
  const parts: string[] = [];
  for (const [key, level] of Object.entries(requires.buildings ?? {})) {
    parts.push(`${BUILDINGS[key as BuildingKey].label} Stufe ${level}`);
  }
  for (const [key, level] of Object.entries(requires.research ?? {})) {
    parts.push(`${RESEARCH[key as ResearchKey].label} Stufe ${level}`);
  }
  return parts.length > 0 ? `Erfordert ${parts.join(' und ')}.` : undefined;
}

function pendingBuildingLevel(game: GameState, key: BuildingKey, planetIndex: number): number {
  let level = buildingLevel(game, key, planetIndex);
  for (const job of game.planets[planetIndex]!.buildQueue) if (job.key === key) level = Math.max(level, job.amount);
  return level;
}

/**
 * What a building delivers now and what the next level would deliver.
 *
 * The owner asked for the production rate to be visible directly on the card.
 * Both numbers come from `productionReport`/`storageReport` on a state with the
 * building set to that level — so the preview is the real number the top bar
 * will show, energy throttling, talents, crater and event buffs included, not a
 * catalogue value that ignores them.
 */
export interface BuildingOutput {
  kind: 'production' | 'energy' | 'storage';
  /** 'power' means Megawatt rather than a stored resource. */
  resource: 'titan' | 'silicate' | 'isotope' | 'power';
  /** Per hour for mines, MW for the solar plant, units of cap for storage. */
  current: number;
  next: number;
  /** Extra Megawatt the next level draws (mines only). */
  energyDelta?: number;
}

/** A copy of `game` with one building on `planetIndex` forced to `level`. */
function withBuildingLevel(game: GameState, key: BuildingKey, level: number, planetIndex: number): GameState {
  return {
    ...game,
    planets: game.planets.map((planet, i) =>
      i === planetIndex ? { ...planet, buildings: { ...planet.buildings, [key]: level } } : planet,
    ),
    moon: { ...game.moon, buildings: { ...game.moon.buildings, [key]: level } },
  };
}

export function buildingOutput(
  game: GameState,
  key: BuildingKey,
  at: number,
  planetIndex = 0,
): BuildingOutput | undefined {
  const def = BUILDINGS[key];
  const level = buildingLevel(game, key, planetIndex);
  const preview = withBuildingLevel(game, key, level + 1, planetIndex);

  if (def.production) {
    const resource = def.production.resource;
    const now = productionReport(game, at);
    const next = productionReport(preview, at);
    return {
      kind: 'production',
      resource,
      current: now.perHour[resource],
      next: next.perHour[resource],
      energyDelta: next.energy.consumed - now.energy.consumed,
    };
  }
  if (def.energy) {
    return {
      kind: 'energy',
      resource: 'power',
      current: productionReport(game, at).energy.produced,
      next: productionReport(preview, at).energy.produced,
    };
  }
  if (def.storage) {
    return {
      kind: 'storage',
      resource: def.storage,
      current: storageReport(game, at)[def.storage],
      next: storageReport(preview, at)[def.storage],
    };
  }
  return undefined;
}

export interface BuildingCard {
  key: BuildingKey;
  label: string;
  description: string;
  level: number;
  /** The level the next enqueue would build (accounts for queued jobs). */
  nextLevel: number;
  state: CardState;
  cost: ReturnType<typeof levelCost>;
  durationSeconds: number;
  requirement?: string;
  /** Active card, but a further level can still be appended to the queue. */
  canQueueMore: boolean;
  /** Current vs. next-level yield — undefined for buildings without output. */
  output?: BuildingOutput;
}

/**
 * `planetIndex` defaults to the home planet. Naniten (`nanoFactory`) only
 * speed up the home planet's OWN queue (galaxy plan Milestone E) — the
 * duration preview reflects that: colonies never see the boosted number.
 */
export function buildingCard(game: GameState, key: BuildingKey, at = game.lastTick, planetIndex = 0): BuildingCard {
  const def = BUILDINGS[key];
  const planet = game.planets[planetIndex]!;
  const nextLevel = pendingBuildingLevel(game, key, planetIndex) + 1;
  const cost = key === 'tradeRelay'
    ? tradeRelayCost(nextLevel, def.baseCost)
    : levelCost(def.baseCost, nextLevel);
  const inQueue = planet.buildQueue.some((j) => j.key === key);

  let state: CardState;
  if (planet.damaged.includes(key)) state = 'damaged';
  else if (inQueue) state = 'active';
  else if (!requirementsMet(game, def.requires, planetIndex)) state = 'locked';
  else if (!canAfford(game, cost)) state = 'insufficient';
  else state = 'available';

  const nanoLevel = planetIndex === 0 ? buildingLevel(game, 'nanoFactory') : 0;
  return {
    key,
    label: def.label,
    description: def.description,
    level: buildingLevel(game, key, planetIndex),
    nextLevel,
    state,
    cost,
    durationSeconds: buildTimeHours(cost, nanoLevel, computeModifiers(game, at).buildSpeed) * 3600,
    requirement: state === 'locked' ? requirementText(def.requires) : undefined,
    canQueueMore: state === 'active' && planet.buildQueue.length < 3 && canAfford(game, cost),
    output: buildingOutput(game, key, at, planetIndex),
  };
}

export interface ResearchNode {
  key: ResearchKey;
  label: string;
  effect: string;
  level: number;
  maxLevel?: number;
  state: CardState;
  cost: ReturnType<typeof levelCost>;
  durationSeconds: number;
  requirement?: string;
  /** Set when this tech is the running job. */
  finishesAt?: number;
}

export function researchNode(game: GameState, key: ResearchKey, at = game.lastTick): ResearchNode {
  const def = RESEARCH[key];
  const activeJob = game.researchQueue.find((j) => j.key === key);
  const nextLevel = game.research[key] + 1;
  const cost = researchLevelCost(def.baseCost, def.factor, nextLevel);

  let state: CardState;
  if (activeJob) state = 'active';
  else if (def.maxLevel !== undefined && game.research[key] >= def.maxLevel) state = 'maxed';
  else if (!requirementsMet(game, def.requires)) state = 'locked';
  else if (game.researchQueue.length > 0) state = 'insufficient'; // slot busy
  else if (!canAfford(game, cost)) state = 'insufficient';
  else state = 'available';

  return {
    key,
    label: def.label,
    effect: def.effect,
    level: game.research[key],
    maxLevel: def.maxLevel,
    state,
    cost,
    durationSeconds:
      researchTimeHours(cost, buildingLevel(game, 'researchLab'), computeModifiers(game, at).researchSpeed) * 3600,
    requirement: state === 'locked' ? requirementText(def.requires) : undefined,
    finishesAt: activeJob?.startedAt ? activeJob.finishesAt : undefined,
  };
}

export interface UnitCard {
  key: UnitKey;
  label: string;
  role: string;
  kind: 'ship' | 'defense';
  stats: { hull: number; shield: number; damage: number; cargo?: number };
  owned: number;
  state: CardState;
  /** Cost for the currently selected batch size. */
  cost: Record<string, number>;
  durationSeconds: number;
  requirement?: string;
}

export function unitCard(game: GameState, key: UnitKey, batch: number, at = game.lastTick, planetIndex = 0): UnitCard {
  const def = UNITS[key];
  const planet = game.planets[planetIndex]!;
  const owned = def.kind === 'ship'
    ? planet.ships[key as ShipKey]
    : planet.defense[key as DefenseKey];

  const cost = Object.fromEntries(
    Object.entries(def.cost).map(([r, v]) => [r, v * batch]),
  ) as Record<string, number>;

  // Event ships are gated on blueprints rather than research: one per hull,
  // won in the casino (GDD §4). Holding enough unlocks the card like any other.
  const blueprints = def.eventOnly ? (game.blueprints[key as ShipKey] ?? 0) : Infinity;

  let state: CardState;
  if (def.eventOnly && blueprints < batch) state = 'locked';
  else if (!requirementsMet(game, def.requires, planetIndex)) state = 'locked';
  else if (
    def.maxCount !== undefined &&
    owned + planet.shipyardQueue
      .filter((j) => j.key === key)
      .reduce((sum, j) => sum + j.amount, 0) >= def.maxCount
  ) state = 'maxed';
  else if (!canAfford(game, cost)) state = 'insufficient';
  else state = 'available';

  return {
    key,
    label: def.label,
    role: def.role,
    kind: def.kind,
    stats: { hull: def.hull, shield: def.shield, damage: def.damage, cargo: def.cargo },
    owned,
    state,
    cost,
    durationSeconds:
      unitTimeHours(
        def.cost,
        planetIndex === 0 ? buildingLevel(game, 'nanoFactory') : 0,
        computeModifiers(game, at).shipyardSpeed,
      ) *
      3600 * batch,
    requirement: def.eventOnly
      ? `Benötigt ${batch} Bauplan${batch === 1 ? '' : 'e'} — ${blueprints === Infinity ? 0 : blueprints} vorhanden. Baupläne gibt es im Casino.`
      : state === 'locked'
        ? requirementText(def.requires)
        : undefined,
  };
}

export interface ResourceBarModel {
  entries: Array<{
    resource: 'titan' | 'silicate' | 'isotope' | 'power' | 'aether' | 'credit' | 'chronos';
    amount: number;
    ratePerHour?: number;
    capacity?: number;
  }>;
  xpForNext: number;
}

/** Top-bar model: stored resources with live rates, energy surplus, currencies. */
export function resourceBarModel(game: GameState, at: number): ResourceBarModel {
  const report = productionReport(game, at);
  const caps = storageReport(game, at);
  return {
    entries: [
      { resource: 'titan', amount: game.resources.titan, ratePerHour: report.perHour.titan, capacity: caps.titan },
      { resource: 'silicate', amount: game.resources.silicate, ratePerHour: report.perHour.silicate, capacity: caps.silicate },
      { resource: 'isotope', amount: game.resources.isotope, ratePerHour: report.perHour.isotope, capacity: caps.isotope },
      { resource: 'power', amount: report.energy.produced - report.energy.consumed },
      ...(game.resources.aether > 0
        ? [{ resource: 'aether' as const, amount: game.resources.aether }]
        : []),
      { resource: 'credit', amount: game.resources.credit },
      { resource: 'chronos', amount: game.resources.chronos },
    ],
    xpForNext: xpForNextLevel(game.level),
  };
}

/**
 * All queue jobs in display order with their queue kind. Building jobs come
 * from EVERY owned planet (galaxy plan Milestone E, Teil 2) — `planetName`
 * is only set for a building job on a planet other than the home one, so
 * the sidebar can disambiguate two same-named jobs running on different
 * planets. Ships/defense/research stay home-planet-only (see the doc
 * comments in actions.ts/tick.ts).
 */
export function allJobs(
  game: GameState,
): Array<{ job: QueueJob; queue: 'building' | 'research' | 'shipyard'; planetName?: string }> {
  return [
    ...game.planets.flatMap((planet, i) =>
      planet.buildQueue.map((job) => ({
        job,
        queue: 'building' as const,
        planetName: i === 0 ? undefined : planet.name,
      })),
    ),
    ...game.researchQueue.map((job) => ({ job, queue: 'research' as const })),
    ...game.planets.flatMap((planet, i) =>
      planet.shipyardQueue.map((job) => ({
        job,
        queue: 'shipyard' as const,
        planetName: i === 0 ? undefined : planet.name,
      })),
    ),
  ];
}

/** Display label for a queue job. */
export function jobLabel(job: QueueJob): string {
  if (job.kind === 'building') return BUILDINGS[job.key as BuildingKey].label;
  if (job.kind === 'research') return RESEARCH[job.key as ResearchKey].label;
  return UNITS[job.key as UnitKey].label;
}

export interface FleetCapacityStatus {
  capacity: number;
  active: number;
  full: boolean;
}

/**
 * Schiffshangar status — shared by every view that sends a fleet
 * (attackCamp, harvestAsteroid, harvestDebris all draw from the same pool,
 * see actions.ts requireFleetCapacity()).
 */
export function fleetCapacityStatus(game: GameState): FleetCapacityStatus {
  const capacity = fleetHangarCapacity(buildingLevel(game, 'shipHangar'));
  const active = game.fleetMissions.length;
  return { capacity, active, full: active >= capacity };
}
